import { useState } from 'react';
import { Phone, MessageSquare, Mail, Send, ChevronDown } from 'lucide-react';
import SEO from '../components/SEO';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    location: '',
    service: '',
    message: ''
  });
  const [status, setStatus] = useState('');

  // Dropdown options based on your services
  const servicesList = [
    "Interior Painting",
    "Exterior Painting",
    "Wood Painting & Polishing",
    "Texture & Royal Designs",
    "White Cement & Waterproofing",
    "Commercial Painting"
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setStatus('Sending...');

    // Constructing WhatsApp Message
    const whatsappMessage = `*New Inquiry - SSS Home Painting*%0A%0A` +
      `*Name:* ${formData.name}%0A` +
      `*Phone:* ${formData.phone}%0A` +
      `*Location:* ${formData.location}%0A` +
      `*Service:* ${formData.service}%0A` +
      `*Message:* ${formData.message}`;

    const whatsappUrl = `https://wa.me/918072127022?text=${whatsappMessage}`;

    // Demo delay before redirect
    setTimeout(() => {
      setStatus('Redirecting to WhatsApp...');
      window.open(whatsappUrl, '_blank');
      setStatus('Message sent successfully!');
      e.target.reset();
      setFormData({ name: '', phone: '', location: '', service: '', message: '' });
    }, 1200);
  };

  const openWhatsAppDirect = () => {
    window.open(`https://wa.me/918072127022?text=Hi SSS Home Painting, I'm interested in your services.`, '_blank');
  };

  return (
    <div className="min-h-screen pt-10">
      <SEO
        title="Contact Us | SSS Home Painting Madurai"
        description="Get a free quote for your painting project in Madurai. Choose your service and contact SSS Home Painting via WhatsApp."
      />

      {/* Header Section */}
      <section className="bg-slate-50 py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <span className="text-orange-600 font-bold tracking-widest uppercase mb-2">Contact Us</span>
          <h1 className="text-4xl md:text-5xl font-bold text-blue-900 mb-6">Let's Color Your Dream</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Ready to transform your space in Madurai? Reach out to us for a free consultation and project estimation.
          </p>
        </div>
      </section>

      <section className="py-20 px-4 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">

          {/* Contact Details Left Side */}
          <div className="space-y-8">
            <h2 className="text-3xl font-bold text-blue-900">Get in Touch</h2>
            <p className="text-gray-600">
              We are based in Madurai and serve all surrounding areas. Fill the form to get a callback or message us directly on WhatsApp.
            </p>

            <div className="space-y-6">
              <div className="flex items-start space-x-4 p-6 bg-white border border-slate-100 rounded-2xl shadow-sm">
                <div className="bg-orange-100 p-3 rounded-lg"><Phone className="text-orange-600" /></div>
                <div>
                  <p className="text-sm font-bold text-gray-500 uppercase tracking-tight">Call Us</p>
                  <p className="text-xl font-bold text-blue-900">+91 80721 27022</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-6 bg-white border border-slate-100 rounded-2xl shadow-sm">
                <div className="bg-blue-100 p-3 rounded-lg"><Mail className="text-blue-900" /></div>
                <div>
                  <p className="text-sm font-bold text-gray-500 uppercase tracking-tight">Email Us</p>
                  <p className="text-xl font-bold text-blue-900 italic">pastranshiyan11@gmail.com</p>
                </div>
              </div>
            </div>

            <button
              onClick={openWhatsAppDirect}
              className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white py-4 rounded-xl font-bold flex items-center justify-center space-x-3 transition-all shadow-lg active:scale-95"
            >
              <MessageSquare size={24} />
              <span>Quick Chat on WhatsApp</span>
            </button>
          </div>

          {/* Inquiry Form Right Side */}
          <div className="bg-white p-8 md:p-10 rounded-3xl shadow-2xl border border-slate-50 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-20 h-20 bg-orange-50 rounded-bl-full -z-0 opacity-50"></div>

            <h3 className="text-2xl font-bold text-blue-900 mb-6 relative z-10">Send an Inquiry</h3>
            <form onSubmit={handleSubmit} className="space-y-5 relative z-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Name</label>
                  <input required name="name" type="text" onChange={handleChange} className="w-full bg-slate-50 border border-slate-200 p-4 rounded-xl focus:ring-2 focus:ring-orange-600 outline-none transition-all" placeholder="Enter name" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                  <input required name="phone" type="tel" onChange={handleChange} className="w-full bg-slate-50 border border-slate-200 p-4 rounded-xl focus:ring-2 focus:ring-orange-600 outline-none transition-all" placeholder="Enter phone" />
                </div>
              </div>

              {/* Service Dropdown */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Required Service</label>
                <div className="relative">
                  <select
                    required
                    name="service"
                    onChange={handleChange}
                    className="w-full bg-slate-50 border border-slate-200 p-4 rounded-xl appearance-none focus:ring-2 focus:ring-orange-600 outline-none transition-all cursor-pointer"
                  >
                    <option value="">Select a service</option>
                    {servicesList.map((service, index) => (
                      <option key={index} value={service}>{service}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={20} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Project Location (Tamil Nadu)</label>
                <input required name="location" type="text" onChange={handleChange} className="w-full bg-slate-50 border border-slate-200 p-4 rounded-xl focus:ring-2 focus:ring-orange-600 outline-none transition-all" placeholder="e.g. Madurai, Trichy, Coimbatore, Chennai, Any" />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Project Details</label>
                <textarea required name="message" rows="3" onChange={handleChange} className="w-full bg-slate-50 border border-slate-200 p-4 rounded-xl focus:ring-2 focus:ring-orange-600 outline-none transition-all" placeholder="Tell us more..."></textarea>
              </div>

              <button
                type="submit"
                className="w-full bg-blue-900 hover:bg-blue-800 text-white py-4 rounded-xl font-bold flex items-center justify-center space-x-2 transition-all shadow-lg active:scale-95"
              >
                <span>{status.includes('Redirecting') ? status : 'Send Inquiry via WhatsApp'}</span>
                {!status.includes('Redirecting') && <Send size={18} />}
              </button>

              {status === 'Message sent successfully!' && (
                <div className="bg-green-50 p-4 rounded-xl">
                  <p className="text-green-600 font-semibold text-center italic">✓ Thank you! Redirecting your request...</p>
                </div>
              )}
            </form>
          </div>

        </div>
      </section>
    </div>
  );
};

export default Contact;